<script setup lang="ts">
import { useCompaniesStore } from '@/store/companies-store';
import { AgreementInfo } from '@/modules/agreements/models';
import { AgreementStatus, Side } from '@/connect';
import { computed } from 'vue';
import { useAgreementsStore } from '@/store/agreements-store';

const { getCompany } = useCompaniesStore();
const agreementsStore = useAgreementsStore();

const props = withDefaults(
  defineProps<{ agreements: AgreementInfo[]; statuses?: AgreementStatus[]; side?: Side }>(),
  { statuses: () => [AgreementStatus.ACTIVE], side: undefined }
);

const agreementsByCounterparty = computed(() => {
  const data = new Map<string, { counterpartyId: string; total: number; selected: number }>();

  // First, filter agreements from the store that match status and side criteria
  const filteredAgreements = agreementsStore.agreements.filter((agreement) => {
    if (!agreement.counterpartyId) return false;
    if (!props.statuses.includes(agreement.status)) return false;
    return !(props.side !== undefined && agreement.side !== props.side);
  });

  // Create a Set of filtered agreement IDs for quick lookup
  const filteredAgreementIds = new Set(filteredAgreements.map((a) => a.id));

  // Count total agreements per counterparty from filtered agreements
  for (const agreement of filteredAgreements) {
    if (!data.has(agreement.counterpartyId)) {
      data.set(agreement.counterpartyId, {
        counterpartyId: agreement.counterpartyId,
        total: 0,
        selected: 0,
      });
    }
    const entry = data.get(agreement.counterpartyId)!;
    ++entry.total;
  }

  // Count selected agreements per counterparty (only those that are in the filtered set)
  for (const agreement of props.agreements) {
    if (!agreement.counterpartyId) continue;
    if (!filteredAgreementIds.has(agreement.id)) continue;

    const entry = data.get(agreement.counterpartyId);
    if (entry) {
      ++entry.selected;
    }
  }

  // Only return counterparties with at least one selected agreement
  return new Map(Array.from(data.entries()).filter(([_, item]) => item.selected > 0));
});
</script>

<template>
  <div>
    <VBadge
      v-for="[counterpartyId, agreement] in agreementsByCounterparty"
      :key="counterpartyId"
      :content="`${agreement.selected} / ${agreement.total}`"
      inline
    >
      {{ getCompany(counterpartyId)?.companyName }}
    </VBadge>
  </div>
</template>

<style scoped lang="scss"></style>
